import { useTranslation } from 'react-i18next'
import classNames from 'classnames'

import { useDateLocale, useTimeZone } from '../../../hooks'
import Text from '../../Text'
import { useCalendarGridContext } from '../CalendarGrid.hooks'
import { getCalendarGridExpandedItems } from '../CalendarGrid.utils'
import { CalendarGridBodyMessage } from '../CalendarGridBodyMessage'
import { CalendarGridCard } from '../CalendarGridCard'

import {
  QA_NAME_CALENDAR_GRID_DAY_POPOVER,
  QA_NAME_CALENDAR_GRID_DAY_POPOVER_EMPTY,
} from './CalendarGridDayPopover.constants'
import { useCalendarGridDayPopoverHandlers } from './CalendarGridDayPopover.hooks'
import {
  getCalendarGridDayPopoverCommonTagItem,
  getCalendarGridDayPopoverItems,
  getHasCalendarGridDayLoadSummary,
  getIsCalendarGridDayEmpty,
} from './CalendarGridDayPopover.utils'
import { CalendarGridDayPopoverCompactRow } from './CalendarGridDayPopoverCompactRow'
import { CalendarGridDayPopoverDate } from './CalendarGridDayPopoverDate'
import { CalendarGridDayPopoverHeader } from './CalendarGridDayPopoverHeader'
import { CalendarGridDayPopoverRow } from './CalendarGridDayPopoverRow'
import { CalendarGridDayPopoverSkeleton } from './CalendarGridDayPopoverSkeleton'

import * as styles from './CalendarGridDayPopover.module.css'

function CalendarGridDayPopover({
  day,
  dayLoad,
  isDayLoadView,
  items,
  onClickItem,
  onClickSecondaryLabel,
  onClose,
}) {
  const { t } = useTranslation()

  const locale = useDateLocale()
  const timezone = useTimeZone()

  const {
    DayPopoverActions,
    ItemRow,
    ItemTag,
    isLoading,
    loadError,
    onReload,
  } = useCalendarGridContext()

  const { handleClickItem, handleClickSecondaryLabel } =
    useCalendarGridDayPopoverHandlers({
      onClickItem,
      onClickSecondaryLabel,
      onClose,
    })

  const dayItems = getCalendarGridExpandedItems(items)

  const Row = isDayLoadView
    ? (ItemRow ?? CalendarGridDayPopoverRow)
    : CalendarGridDayPopoverCompactRow

  const commonTagItem = ItemTag
    ? getCalendarGridDayPopoverCommonTagItem(dayItems)
    : null

  const isTagInHeader =
    !isDayLoadView && !getHasCalendarGridDayLoadSummary(dayLoad)

  const headerTagItem = isTagInHeader ? commonTagItem : null

  const cardTagItem = isDayLoadView ? commonTagItem : headerTagItem

  return (
    <CalendarGridCard
      Actions={DayPopoverActions}
      HeaderContent={CalendarGridDayPopoverHeader}
      actionsProps={{ day, dayLoad, items: dayItems, onClose }}
      headerContentProps={{ dayLoad, headerTagItem, isDayLoadView }}
      qaName={QA_NAME_CALENDAR_GRID_DAY_POPOVER}
      onClose={onClose}
    >
      {isDayLoadView ? null : <CalendarGridDayPopoverDate day={day} />}
      {isLoading ? (
        <CalendarGridDayPopoverSkeleton />
      ) : loadError ? (
        <CalendarGridBodyMessage
          action={{ callback: onReload, text: t('Calendar.RetryKey') }}
          className={styles.message}
          text={t('Calendar.ErrorLoadKey')}
        />
      ) : (
        <>
          <div
            className={classNames(styles.rows, {
              [styles.compact]: !isDayLoadView,
            })}
          >
            {getCalendarGridDayPopoverItems(dayItems).map(item => (
              <Row
                isTagShown={!cardTagItem}
                item={item}
                key={item.id}
                locale={locale}
                timezone={timezone}
                onClickItem={handleClickItem}
                onClickSecondaryLabel={
                  onClickSecondaryLabel && handleClickSecondaryLabel
                }
              />
            ))}
          </div>
          {getIsCalendarGridDayEmpty(dayItems) ? (
            <Text isSecondary qaName={QA_NAME_CALENDAR_GRID_DAY_POPOVER_EMPTY}>
              {t('Calendar.EmptyDayKey')}
            </Text>
          ) : null}
        </>
      )}
      {isDayLoadView ? <CalendarGridDayPopoverDate day={day} /> : null}
    </CalendarGridCard>
  )
}

export default CalendarGridDayPopover
